import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import Navigation from './components/Navigation';
import HomePage from './components/HomePage'; // Page d'accueil avec vraies données Supabase
import SplashScreen from './components/SplashScreen'; // Page d'accueil animée
import MovieDetails from './components/MovieDetails';
import ProfilePage from './components/Profile/ProfilePage'; // Page de profil
import AddVideoPageExtended from './components/Creator/AddVideoPageExtended'; // Ajout de vidéo
import MyVideosExtended from './components/Creator/MyVideosExtended';
import WatchlistPage from './components/WatchlistPage'; // Ma Watchlist
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import DevTools from './components/DevTools'; // Outils de développement
import "./App.css";

// Import des styles globaux pour les effets néon
import './styles/globals.css';

// Page de visionnage avec lecteur vidéo réel
const WatchPage = () => {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/${id}`);
        const data = await response.json();
        if (data.success) {
          setVideo(data.data);
          console.log('🎬 Vidéo chargée pour lecture:', data.data.title, data.data.playback_url);
        }
      } catch (error) {
        console.error('Erreur chargement vidéo:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchVideo();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400 mx-auto mb-4"></div>
          <p>Chargement de la vidéo...</p>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4 text-red-400">Vidéo non trouvée</h1>
          <button 
            onClick={() => window.history.back()} 
            className="text-pink-400 hover:text-pink-300"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <button 
            onClick={() => window.history.back()} 
            className="text-pink-400 hover:text-pink-300 flex items-center space-x-2"
          >
            <span>← Retour</span>
          </button>
        </div>
        
        <div className="bg-gray-900 rounded-lg overflow-hidden">
          <div className="relative aspect-video bg-black flex items-center justify-center">
            {video.playback_url ? (
              <video 
                controls 
                autoPlay
                className="w-full h-full"
                poster={video.thumbnail_url}
                preload="metadata"
              >
                <source src={video.playback_url} type="application/x-mpegURL" />
                <source src={video.playback_url.replace('.m3u8', '.mp4')} type="video/mp4" />
                <p className="text-white">
                  Votre navigateur ne supporte pas la lecture vidéo.
                  <a href={video.playback_url} className="text-pink-400 ml-2">
                    Ouvrir dans un lecteur externe
                  </a>
                </p>
              </video>
            ) : (
              <div className="text-center">
                <div className="text-yellow-400 text-6xl mb-4">⏳</div>
                <h3 className="text-xl font-bold mb-2">Vidéo en traitement</h3>
                <p className="text-gray-400">Cette vidéo est encore en cours de traitement par Cloudflare Stream.</p>
                <p className="text-gray-400 text-sm mt-2">Statut: {video.status}</p>
                {video.cloudflare_video_id && (
                  <p className="text-gray-500 text-xs mt-1">ID Cloudflare: {video.cloudflare_video_id}</p>
                )}
              </div>
            )}
          </div>
          
          <div className="p-6">
            <h1 className="text-2xl font-bold mb-4">{video.title}</h1>
            
            <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
              {video.release_year && <span>{video.release_year}</span>}
              {video.created_by && (
                <>
                  <span>•</span>
                  <span>Créé par {video.created_by}</span>
                </>
              )}
              {video.duration_seconds && (
                <>
                  <span>•</span>
                  <span>{Math.floor(video.duration_seconds / 60)} min</span>
                </>
              )}
              <span>•</span>
              <span className={`${video.status === 'ready' ? 'text-green-400' : 'text-yellow-400'}`}>
                {video.status === 'ready' ? 'Disponible' : 'En traitement'}
              </span>
            </div>
            
            {video.description && (
              <p className="text-gray-300 mb-6">{video.description}</p>
            )}

            {video.playback_url && (
              <div className="text-xs text-gray-500">
                <p>🎬 URL de lecture: {video.playback_url}</p>
                {video.thumbnail_url && <p>🖼️ Vignette: {video.thumbnail_url}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FilmsPage = () => (
  <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-3xl font-bold mb-4">Films IA</h1>
      <p className="text-gray-400">Page des films en cours de développement</p>
    </div>
  </div>
);

const SeriesPage = () => (
  <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-3xl font-bold mb-4">Séries IA</h1>
      <p className="text-gray-400">Page des séries en cours de développement</p>
    </div>
  </div>
);

const NewReleasesPage = () => (
  <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-3xl font-bold mb-4">Nouveautés</h1>
      <p className="text-gray-400">Page des nouveautés en cours de développement</p>
    </div>
  </div>
);

function App() {
  return (
    <div className="App bg-black min-h-screen">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Page splash (première page) */}
            <Route path="/" element={<SplashScreen />} />
            
            {/* Pages avec navigation */}
            <Route path="/home" element={
              <>
                <Navigation />
                <HomePage />
              </>
            } />
            <Route path="/films" element={
              <>
                <Navigation />
                <FilmsPage />
              </>
            } />
            <Route path="/series" element={
              <>
                <Navigation />
                <SeriesPage />
              </>
            } />
            <Route path="/nouveautes" element={
              <>
                <Navigation />
                <NewReleasesPage />
              </>
            } />
            <Route path="/details/:id" element={
              <>
                <Navigation />
                <MovieDetails />
              </>
            } />
            <Route path="/movie/:id" element={
              <>
                <Navigation />
                <MovieDetails />
              </>
            } />
            <Route path="/watch/:id" element={
              <>
                <Navigation />
                <WatchPage />
              </>
            } />
            
            {/* Pages de profil et création */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/add" element={<AddVideoPageExtended />} />
            <Route path="/my-videos" element={<MyVideosExtended />} />
            <Route path="/my-videos/full-studio" element={<MyVideosExtended />} />
            <Route path="/watchlist" element={<WatchlistPage />} />
            
            {/* Pages d'authentification sans navigation */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
          </Routes>
          <Toaster />
          <DevTools />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;